import moment from 'moment';

import {
  getApiData,
  getErrorReason,
  hasData,
  isBusinessError,
} from 'scenes/common/helpers/apiData';
import {
  Address,
  BuildingLevel,
  BuildingType,
  CoverType,
  Excess,
  PaymentFrequency,
  PolicyIssued,
  SumInsuredType,
  ValueRange,
  YesNoAnswer,
} from 'scenes/common/types';

import { InsuranceOption, LandlordFunnelState } from 'scenes/landlord/types';
import {
  PropertyPurposeType,
  KickOutChoice,
} from 'scenes/landlord/flows/quoteDetails/types';
import { defaultExcess } from 'scenes/landlord/flows/coverOptions/defaults';
import { defaultInsurancePremium } from '../flows/quoteDetails/defaults';
import { ExcessType, OptionalExtra } from '../flows/coverOptions/types';
import { getCampaignAdjustments } from './campaignSelectors';
import { LandlordFlowType } from '../flows/types';

export const getCoverType = (state: LandlordFunnelState): CoverType =>
  state.quoteDetails.coverType;

export const hasBuildingCoverType = (state: LandlordFunnelState): boolean =>
  [CoverType.BuildingAndContents, CoverType.Building].includes(
    getCoverType(state)
  );

export const hasContentsCoverType = (state: LandlordFunnelState): boolean =>
  [CoverType.BuildingAndContents, CoverType.Contents].includes(
    state.quoteDetails.coverType
  );

export const isBuildingAndContentsCoverType = (
  state: LandlordFunnelState
): boolean => getCoverType(state) === CoverType.BuildingAndContents;

export const isBuildingCoverType = (state: LandlordFunnelState): boolean =>
  getCoverType(state) === CoverType.Building;

export const isContentsCoverType = (state: LandlordFunnelState): boolean =>
  getCoverType(state) === CoverType.Contents;

export const getPolicyStartDate = (state: LandlordFunnelState): string =>
  state.quoteDetails.policyStartDate;

export const getMainAddress = (state: LandlordFunnelState): Address =>
  state.quoteDetails.mainAddress;

export const hasFlybuys = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.hasFlybuys;

export const isFlybuysMember = (state: LandlordFunnelState): boolean =>
  state.quoteDetails.hasFlybuys === YesNoAnswer.Yes;

export const getFlybuysNumber = (state: LandlordFunnelState): string =>
  (isFlybuysMember(state) && state.quoteDetails.flybuysNumber) || '';

export const getQuoteNumber = (state: LandlordFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).payload.policy.number;
  }
  if (isBusinessError(state.apiResponses.quoteDetails)) {
    return getErrorReason(state.apiResponses.quoteDetails)
      .quoteNumber as string;
  }
  return '';
};

export const getQuoteReference = (state: LandlordFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).payload.policy?.reference ||
      ''
    );
  }
  return '';
};

export const hasQuoteNumber = (state: LandlordFunnelState): boolean =>
  Boolean(getQuoteNumber(state));

export const getSelectedCoverType = (state: LandlordFunnelState) =>
  state.quoteDetails.coverType;

export const getSelectedInsuranceOption = (
  state: LandlordFunnelState
): InsuranceOption =>
  getApiData(state.apiResponses.quoteDetails)?.insuranceOptions?.find(
    (opt) => opt.coverType === getSelectedCoverType(state)
  ) ||
  getApiData(state.apiResponses.quoteDetails)?.insuranceOptions?.[0] ||
  defaultInsurancePremium;

export const getSaveQuoteEmailAddress = (
  state: LandlordFunnelState
): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).saveQuoteEmailAddress || ''
    );
  }
  return '';
};

export const getOwnedProperty = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.ownedProperty;

export const isPropertyOwned = (state: LandlordFunnelState): boolean =>
  state.quoteDetails.ownedProperty === YesNoAnswer.Yes;

export const getPurposeOfProperty = (
  state: LandlordFunnelState
): PropertyPurposeType => state.quoteDetails.purposeOfProperty;

export const getKickOutChoice = (state: LandlordFunnelState): KickOutChoice =>
  state.quoteDetails.kickOutChoice;

export const isKickOutChangeOfMind = (state: LandlordFunnelState): boolean =>
  state.quoteDetails.kickOutChoice === KickOutChoice.ChangeOfMind;

export const getPropertyUsedForBusiness = (
  state: LandlordFunnelState
): YesNoAnswer => state.quoteDetails.propertyUsedForBusiness;

export const isPropertyUsedForBusiness = (
  state: LandlordFunnelState
): boolean => state.quoteDetails.propertyUsedForBusiness === YesNoAnswer.Yes;

export const getYearsInsured = (state: LandlordFunnelState): number =>
  state.quoteDetails.yearsInsured;

export const hasRentalAgreement = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.hasRentalAgreement;

export const isRentalAgreement = (state: LandlordFunnelState): boolean =>
  state.quoteDetails.hasRentalAgreement === YesNoAnswer.Yes;

export const getWeeklyRentPaid = (state: LandlordFunnelState): string =>
  state.quoteDetails.weeklyRentPaid;

export const isRental = (state: LandlordFunnelState): boolean =>
  getPurposeOfProperty(state) === PropertyPurposeType.Rental;

export const isShortTermRental = (state: LandlordFunnelState): boolean =>
  getPurposeOfProperty(state) === PropertyPurposeType.ShortTermRental;

export const getClaimsMade = (state: LandlordFunnelState): number =>
  state.quoteDetails.claimsMade;

export const hasClaimsMade = (state: LandlordFunnelState): boolean =>
  state.quoteDetails.claimsMade > 0;

export const getBrokenInClaims = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.brokenInClaims;

export const hasBreakInClaims = (state: LandlordFunnelState): boolean =>
  getBrokenInClaims(state) === YesNoAnswer.Yes;

export const getAdditionalSecurityInstalled = (
  state: LandlordFunnelState
): YesNoAnswer => state.quoteDetails.additionalSecurityInstalled;

export const hasAdditionalSecurityInstalled = (
  state: LandlordFunnelState
): boolean => getAdditionalSecurityInstalled(state) === YesNoAnswer.Yes;

export const getNumberOfBedrooms = (state: LandlordFunnelState): number =>
  state.quoteDetails.numberOfBedrooms;

export const getRoofMaterial = (state: LandlordFunnelState): string =>
  state.quoteDetails.roofMaterial;

export const getWallMaterial = (state: LandlordFunnelState): string =>
  state.quoteDetails.wallMaterial;

export const getSumInsuredType = (state: LandlordFunnelState): SumInsuredType =>
  state.quoteDetails.sumInsuredType;

export const usingEstimatedSumInsured = (state: LandlordFunnelState): boolean =>
  getSumInsuredType(state) === SumInsuredType.Estimated;

export const usingAgreedSumInsured = (state: LandlordFunnelState): boolean =>
  getSumInsuredType(state) === SumInsuredType.Agreed;

export const getPolicyHolderDateOfBirth = (
  state: LandlordFunnelState
): string => state.quoteDetails.policyHolderDateOfBirth;

export const getBusinessOwnerDateOfBirth = (
  state: LandlordFunnelState
): string => state.quoteDetails.businessOwnerDateOfBirth;

export const getRecentInsurer = (state: LandlordFunnelState): string =>
  state.quoteDetails.recentInsurer;

export const getBuildingType = (state: LandlordFunnelState): BuildingType =>
  state.quoteDetails.buildingType;

export const getFreestandingMetreGap = (
  state: LandlordFunnelState
): YesNoAnswer => state.quoteDetails.freestandingMetreGap;

export const isFreestandingAboveGround = (
  state: LandlordFunnelState
): boolean => getFreestandingMetreGap(state) === YesNoAnswer.Yes;

export const getFreestandingStorey = (
  state: LandlordFunnelState
): YesNoAnswer => state.quoteDetails.freestandingStorey;

export const isFreestandingMultiStorey = (
  state: LandlordFunnelState
): boolean => getFreestandingStorey(state) === YesNoAnswer.Yes;

export const getTerraceStorey = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.terraceStorey;

export const isTerraceMultiStorey = (state: LandlordFunnelState): boolean =>
  getTerraceStorey(state) === YesNoAnswer.Yes;

export const getTerraceStrata = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.terraceStrata;

export const isTerraceUnderStrata = (state: LandlordFunnelState): boolean =>
  getTerraceStrata(state) === YesNoAnswer.Yes;

export const getTownhouseStorey = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.townhouseStorey;

export const isTownhouseMultiStorey = (state: LandlordFunnelState): boolean =>
  getTownhouseStorey(state) === YesNoAnswer.Yes;

export const getTownhouseStrata = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.townhouseStrata;

export const isTownhouseUnderStrata = (state: LandlordFunnelState): boolean =>
  getTownhouseStrata(state) === YesNoAnswer.Yes;

export const getUnitStorey = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.unitStorey;

export const isUnitMultiStorey = (state: LandlordFunnelState): boolean =>
  getUnitStorey(state) === YesNoAnswer.Yes;

export const getUnitLevel = (state: LandlordFunnelState): BuildingLevel =>
  state.quoteDetails.unitLevel;

export const getUnitStrata = (state: LandlordFunnelState): YesNoAnswer =>
  state.quoteDetails.unitStrata;

export const isUnitUnderStrata = (state: LandlordFunnelState): boolean =>
  getUnitStrata(state) === YesNoAnswer.Yes;

export const isUnitBuildingType = (state: LandlordFunnelState): boolean =>
  getBuildingType(state) === BuildingType.Unit;

export const isUnitWithContentsOnlyCover = (
  state: LandlordFunnelState
): boolean => isUnitBuildingType(state) && !hasBuildingCoverType(state);

export const getSecurityFeatures = (state: LandlordFunnelState): string[] =>
  state.quoteDetails.securityFeatures;

export const getPropertyBuiltYear = (state: LandlordFunnelState): string =>
  state.quoteDetails.propertyBuiltYear;

export const getCertificateOfOccupancy = (
  state: LandlordFunnelState
): YesNoAnswer => state.quoteDetails.certificateOfOccupancy;

export const certificateOfOccupancyAnswerRequired = (buildYear: string) =>
  Number(buildYear) > moment().year() - 2;

export const hasCertificateOfOccupancy = (
  state: LandlordFunnelState
): boolean =>
  certificateOfOccupancyAnswerRequired(getPropertyBuiltYear(state)) &&
  getCertificateOfOccupancy(state) === YesNoAnswer.Yes;

export const getSelectedBuildingExcess = (
  state: LandlordFunnelState
): Excess => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).userSelections
        ?.buildingExcess ?? defaultExcess
    );
  }
  return defaultExcess;
};

export const getBuildingExcesses = (state: LandlordFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails)?.buildingExcesses ?? [];
  }
  return [];
};

export const getStandardBuildingExcesses = (state: LandlordFunnelState) =>
  getBuildingExcesses(state).filter(
    (excess) => excess.type === ExcessType.Standard
  );

export const getAdditionalBuildingExcesses = (state: LandlordFunnelState) =>
  getBuildingExcesses(state).filter(
    (excess) => excess.type === ExcessType.Additional
  );

export const getSelectedContentsExcess = (
  state: LandlordFunnelState
): Excess => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).userSelections
        ?.contentsExcess ?? defaultExcess
    );
  }
  return defaultExcess;
};

export const getContentsExcesses = (state: LandlordFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails)?.contentsExcesses ?? [];
  }
  return [];
};

export const getStandardContentsExcesses = (state: LandlordFunnelState) =>
  getContentsExcesses(state).filter(
    (excess) => excess.type === ExcessType.Standard
  );

export const getAdditionalContentsExcesses = (state: LandlordFunnelState) =>
  getContentsExcesses(state).filter(
    (excess) => excess.type === ExcessType.Additional
  );

export const getSelectedExcesses = (state: LandlordFunnelState): Excess[] => {
  const excesses = [];
  if (hasBuildingCoverType(state)) {
    excesses.push(getSelectedBuildingExcess(state));
  }

  if (hasContentsCoverType(state)) {
    excesses.push(getSelectedContentsExcess(state));
  }

  return excesses;
};

export const getPolicyIssued = (state: LandlordFunnelState): PolicyIssued =>
  state.quoteDetails.policyIssued ?? PolicyIssued.Unspecified;

export const isPolicyIssuedToPerson = (state: LandlordFunnelState): boolean =>
  getPolicyIssued(state) === PolicyIssued.Person;

export const getFirstInstalment = (state: LandlordFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).recalculatedInstalments
        ?.firstInstalment ??
      getSelectedInsuranceOption(state)?.premium.firstInstalment ??
      ''
    );
  }
  return '';
};

export const getMonthlyInstalment = (state: LandlordFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).recalculatedInstalments
        ?.monthlyInstalment ??
      getSelectedInsuranceOption(state)?.premium.monthlyInstalment ??
      ''
    );
  }
  return '';
};

export const getReceiptNumber = (state: LandlordFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).receiptNumber ?? '';
  }
  return '';
};

export const getPrefilledCampaignId = (state: LandlordFunnelState): string =>
  state.quoteDetails.prefilledCampaignId;

export const getColesCampaignCode = (state: LandlordFunnelState): string =>
  state.quoteDetails.colesCampaignCode;

export const getPrefilledPromoCode = (state: LandlordFunnelState): string =>
  state.quoteDetails.prefilledPromoCode;

export const getPromoCode = (state: LandlordFunnelState): string =>
  state.quoteDetails.promoCode;

export const hasPromoCode = (state: LandlordFunnelState): boolean =>
  getPromoCode(state).length !== 0;

export const getAgreedSumInsured = (state: LandlordFunnelState): string =>
  state.quoteDetails.agreedBuildingSumInsured;

export const getAgreedSumInsuredRange = (
  state: LandlordFunnelState
): ValueRange => state.quoteDetails.agreedSumInsuredRange;

export const getContentsSumInsured = (state: LandlordFunnelState): string =>
  state.quoteDetails.contentsSumInsured;

export const getEstimatedSumInsured = (state: LandlordFunnelState): string =>
  state.quoteDetails.estimatedSumInsured;

export const getBuildingSumInsured = (state: LandlordFunnelState) =>
  usingEstimatedSumInsured(state)
    ? getEstimatedSumInsured(state)
    : getAgreedSumInsured(state);

export const getSelectedExtras = (state: LandlordFunnelState): string[] =>
  state.coverOptions.selectedExtras;

export const getIncludedExtras = (state: LandlordFunnelState): string[] => [
  ...getCampaignAdjustments(state)
    .filter(({ percentageValue }) => percentageValue === 100)
    .map(({ type }) => type),
];

export const getExtras = (state: LandlordFunnelState): string[] => [
  ...getSelectedExtras(state),
  ...getIncludedExtras(state),
];

export const isDamagedCausedByTenantsExtraSelected = (
  state: LandlordFunnelState
): boolean => getSelectedExtras(state).includes(OptionalExtra.TenantDamage);

export const isLossOfRentExtraSelected = (
  state: LandlordFunnelState
): boolean => getSelectedExtras(state).includes(OptionalExtra.LossOfRent);

export const isTenantsRentDefaultExtraSelected = (
  state: LandlordFunnelState
): boolean => getSelectedExtras(state).includes(OptionalExtra.RentDefault);

export const getPreselectedPaymentFrequency = (
  state: LandlordFunnelState
): PaymentFrequency => {
  if (state.flow === LandlordFlowType.CoverOptions) {
    return state.coverOptions.preselectedPaymentFrequency;
  }
  if (state.flow === LandlordFlowType.PolicyDetails) {
    return state.policyDetails.preselectedPaymentFrequency;
  }
  return PaymentFrequency.Unspecified;
};

export const isMonthlyPaymentPreselected = (
  state: LandlordFunnelState
): boolean =>
  getPreselectedPaymentFrequency(state) === PaymentFrequency.Monthly;

export const getPreselectedPrice = (state: LandlordFunnelState): string => {
  const {
    premium: { annualPrice, monthlyInstalment },
  } = getSelectedInsuranceOption(state);
  return isMonthlyPaymentPreselected(state) ? monthlyInstalment : annualPrice;
};
